import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  FaSave, FaTimes, FaUpload, FaTrash, FaImage, 
  FaMobile, FaTag, FaDollarSign, FaMicrochip, FaMemory,
  FaBatteryFull, FaDesktop, FaCamera, FaWifi, FaCheckCircle
} from 'react-icons/fa';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import { brandService } from '../../services/brandService';
import { uploadService } from '../../services/uploadService';
import toast from 'react-hot-toast';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [activeTab, setActiveTab] = useState('basic');
  
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: '',
    description: '',
    shortDescription: '',
    pricing: {
      originalPrice: '',
      salePrice: '',
      costPrice: '',
      currency: 'PKR',
    },
    specifications: {
      ram: '',
      storage: '',
      processor: '',
      battery: '',
      display: '',
      operatingSystem: '',
      camera: { rear: '', front: '' },
      connectivity: { sim: '', network: '', wifi: '', bluetooth: '' },
    },
    inventory: {
      stock: 0,
      lowStockThreshold: 5,
    },
    warranty: {
      available: true,
      duration: '1 Year',
      type: 'Manufacturer Warranty',
    },
    ptaApproved: true,
    condition: 'new',
    status: {
      featured: false,
      trending: false,
      active: true,
      newArrival: false,
    },
    tags: [],
    images: [],
  });

  useEffect(() => {
    fetchDropdowns();
    if (isEditing) {
      fetchProduct();
    }
  }, [id]);

  const fetchDropdowns = async () => {
    try {
      const [catsRes, brandsRes] = await Promise.all([
        categoryService.getCategories(),
        brandService.getBrands(),
      ]);
      setCategories(catsRes.data || []);
      setBrands(brandsRes.data || []);
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error('Failed to load categories and brands');
    }
  };

  const fetchProduct = async () => {
    setLoading(true);
    try {
      const response = await productService.getProductById(id);
      const p = response.data;

      // Normalize populated refs to plain IDs for <select> inputs
      const brandId = typeof p.brand === 'object' ? p.brand?._id : p.brand;
      const categoryId = typeof p.category === 'object' ? p.category?._id : p.category;

      // Ensure tags and colors are flat string arrays (not objects)
      const normalizeTags = (arr) =>
        Array.isArray(arr) ? arr.map(t => (typeof t === 'string' ? t : String(t))).filter(Boolean) : [];

      setFormData({
        ...p,
        brand: brandId || '',
        category: categoryId || '',
        tags: normalizeTags(p.tags),
        colors: normalizeTags(p.colors),
        pricing: {
          originalPrice: p.pricing?.originalPrice || '',
          salePrice: p.pricing?.salePrice || '',
          costPrice: p.pricing?.costPrice || '',
          currency: p.pricing?.currency || 'PKR',
        },
        specifications: {
          ram: p.specifications?.ram || '',
          storage: p.specifications?.storage || '',
          processor: p.specifications?.processor || '',
          battery: p.specifications?.battery || '',
          display: p.specifications?.display || '',
          operatingSystem: p.specifications?.operatingSystem || '',
          camera: {
            rear: p.specifications?.camera?.rear || '',
            front: p.specifications?.camera?.front || '',
          },
          connectivity: {
            sim: p.specifications?.connectivity?.sim || '',
            network: p.specifications?.connectivity?.network || '',
            wifi: p.specifications?.connectivity?.wifi || '',
            bluetooth: p.specifications?.connectivity?.bluetooth || '',
          },
        },
        inventory: {
          stock: p.inventory?.stock || 0,
          lowStockThreshold: p.inventory?.lowStockThreshold || 5,
        },
        warranty: {
          available: p.warranty?.available ?? true,
          duration: p.warranty?.duration || '1 Year',
          type: p.warranty?.type || 'Manufacturer Warranty',
        },
        status: {
          featured: p.status?.featured || false,
          trending: p.status?.trending || false,
          active: p.status?.active ?? true,
          newArrival: p.status?.newArrival || false,
        },
        images: Array.isArray(p.images) ? p.images : [],
      });
    } catch (error) {
      toast.error('Failed to load product');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      if (parts.length === 2) {
        const [parent, child] = parts;
        setFormData(prev => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === 'checkbox' ? checked : value,
          },
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleSpecChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        [field]: value,
      },
    }));
  };

  const handleCameraChange = (type, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: {
        ...prev.specifications,
        camera: {
          ...prev.specifications.camera,
          [type]: value,
        },
      },
    }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    const toastId = toast.loading(`Uploading ${files.length} image(s)...`);
    
    try {
      const uploadedImages = [];
      for (const file of files) {
        const response = await uploadService.uploadImage(file);
        uploadedImages.push({
          url: response.data.url,
          public_id: response.data.public_id,
          isPrimary: formData.images.length === 0 && uploadedImages.length === 0,
          altText: formData.name || 'Product image',
        });
      }
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedImages],
      }));
      toast.success(`${files.length} image(s) uploaded successfully`, { id: toastId });
    } catch (error) {
      toast.error('Failed to upload images', { id: toastId });
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = async (index) => {
    const imageToRemove = formData.images[index];
    if (imageToRemove.public_id) {
      try {
        await uploadService.deleteImage(imageToRemove.public_id);
      } catch (error) {
        console.error('Failed to delete from cloudinary:', error);
      }
    }
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    toast.success('Image removed');
  };

  const handleSetPrimary = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.map((img, i) => ({
        ...img,
        isPrimary: i === index,
      })),
    }));
    toast.success('Primary image updated');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name) {
      toast.error('Please enter product name');
      return;
    }
    if (!formData.brand) {
      toast.error('Please select a brand');
      return;
    }
    if (!formData.category) {
      toast.error('Please select a category');
      return;
    }
    if (!formData.pricing.salePrice) {
      toast.error('Please enter sale price');
      return;
    }
    if (!formData.description) {
      toast.error('Please enter product description');
      return;
    }
    
    setLoading(true);
    const toastId = toast.loading(isEditing ? 'Updating product...' : 'Creating product...');
    
    try {
      // Build clean data with proper number conversions
      const submitData = {
        name: formData.name,
        brand: formData.brand,
        category: formData.category,
        description: formData.description,
        shortDescription: formData.shortDescription,
        condition: formData.condition,
        ptaApproved: formData.ptaApproved,
        pricing: {
          ...formData.pricing,
          originalPrice: Number(formData.pricing.originalPrice) || Number(formData.pricing.salePrice) || 0,
          salePrice: Number(formData.pricing.salePrice) || 0,
          costPrice: Number(formData.pricing.costPrice) || 0,
        },
        specifications: formData.specifications,
        inventory: {
          ...formData.inventory,
          stock: Number(formData.inventory.stock) || 0,
          lowStockThreshold: Number(formData.inventory.lowStockThreshold) || 5,
        },
        warranty: formData.warranty,
        status: formData.status,
        tags: Array.isArray(formData.tags) ? formData.tags.filter(t => typeof t === 'string' && t.trim()) : [],
        colors: Array.isArray(formData.colors) ? formData.colors.filter(c => typeof c === 'string' && c.trim()) : [],
        seo: formData.seo || {},
        // Clean images: only send schema-expected fields
        images: (formData.images || []).map(img => ({
          url: img.url,
          public_id: img.public_id,
          isPrimary: img.isPrimary || false,
          altText: img.altText || '',
        })),
      };

      if (isEditing) {
        await productService.updateProduct(id, submitData);
        toast.success('Product updated successfully!', { id: toastId });
      } else {
        await productService.createProduct(submitData);
        toast.success('Product created successfully!', { id: toastId });
      }
      navigate('/admin/products');
    } catch (error) {
      console.error('Product save error:', error);
      const serverMsg = error.response?.data?.message;
      toast.error(serverMsg || (isEditing ? 'Failed to update product' : 'Failed to create product'), { id: toastId });
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info', icon: <FaMobile /> },
    { id: 'pricing', label: 'Pricing', icon: <FaDollarSign /> },
    { id: 'specs', label: 'Specifications', icon: <FaMicrochip /> },
    { id: 'images', label: 'Images', icon: <FaImage /> },
    { id: 'status', label: 'Status', icon: <FaCheckCircle /> },
  ];

  if (loading && isEditing) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product...</p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6 mb-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-white">
              {isEditing ? '✏️ Edit Product' : '➕ Add New Product'}
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-1">
              {isEditing ? 'Update product information' : 'Fill in the details to add a new product'}
            </p>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/admin/products')}
              className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <FaTimes /> Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all flex items-center gap-2 disabled:opacity-50"
            >
              <FaSave /> {loading ? 'Saving...' : 'Save Product'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft mb-6 overflow-x-auto">
        <div className="flex border-b border-gray-200 dark:border-gray-700 min-w-max">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 flex items-center gap-2 font-medium transition-all ${
                activeTab === tab.id
                  ? 'text-primary-600 border-b-2 border-primary-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-primary-600'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-soft p-6">
        
        {/* Basic Information Tab */}
        {activeTab === 'basic' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <FaMobile className="text-primary-600" /> Basic Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Product Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., iPhone 15 Pro Max"
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Brand <span className="text-red-500">*</span>
                </label>
                <select
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="">Select a brand</option>
                  {brands.map(b => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Category <span className="text-red-500">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="">Select a category</option>
                  {categories.map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Condition
                </label>
                <select
                  name="condition"
                  value={formData.condition}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                >
                  <option value="new">🆕 New</option>
                  <option value="used">📱 Used</option>
                  <option value="open_box">📦 Open Box</option>
                  <option value="refurbished">🔧 Refurbished</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Short Description
                </label>
                <input
                  type="text"
                  name="shortDescription"
                  value={formData.shortDescription}
                  onChange={handleChange}
                  placeholder="Brief description (max 200 characters)"
                  maxLength="200"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Full Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows="5"
                  placeholder="Detailed product description with features and benefits..."
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-none"
                />
              </div>
            </div>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <FaDollarSign className="text-primary-600" /> Pricing Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Original Price
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">RS:</span>
                  <input
                    type="number"
                    name="pricing.originalPrice"
                    value={formData.pricing.originalPrice}
                    onChange={handleChange}
                    placeholder="e.g., 499999"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Original price before discount</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Sale Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">RS:</span>
                  <input
                    type="number"
                    name="pricing.salePrice"
                    value={formData.pricing.salePrice}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 459999"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Cost Price <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">RS:</span>
                  <input
                    type="number"
                    name="pricing.costPrice"
                    value={formData.pricing.costPrice}
                    onChange={handleChange}
                    required
                    placeholder="e.g., 380000"
                    className="w-full pl-8 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">Your purchase cost (for profit calculation)</p>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Initial Stock
                </label>
                <input
                  type="number"
                  name="inventory.stock"
                  value={formData.inventory.stock}
                  onChange={handleChange}
                  placeholder="e.g., 50"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  name="inventory.lowStockThreshold"
                  value={formData.inventory.lowStockThreshold}
                  onChange={handleChange}
                  placeholder="e.g., 5"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
                <p className="text-xs text-gray-500 mt-1">Alert when stock reaches this number</p>
              </div>
            </div>
          </div>
        )}

        {/* Specifications Tab */}
        {activeTab === 'specs' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <FaMicrochip className="text-primary-600" /> Technical Specifications
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FaMemory className="inline mr-2" /> RAM
                </label>
                <input
                  value={formData.specifications.ram}
                  onChange={(e) => handleSpecChange('ram', e.target.value)}
                  placeholder="e.g., 8GB, 12GB"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Storage
                </label>
                <input
                  value={formData.specifications.storage}
                  onChange={(e) => handleSpecChange('storage', e.target.value)}
                  placeholder="e.g., 128GB, 256GB, 512GB"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Processor
                </label>
                <input
                  value={formData.specifications.processor}
                  onChange={(e) => handleSpecChange('processor', e.target.value)}
                  placeholder="e.g., A17 Pro, Snapdragon 8 Gen 3"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FaBatteryFull className="inline mr-2" /> Battery
                </label>
                <input
                  value={formData.specifications.battery}
                  onChange={(e) => handleSpecChange('battery', e.target.value)}
                  placeholder="e.g., 5000mAh"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FaDesktop className="inline mr-2" /> Display
                </label>
                <input
                  value={formData.specifications.display}
                  onChange={(e) => handleSpecChange('display', e.target.value)}
                  placeholder="e.g., 6.7-inch Super Retina XDR"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Operating System
                </label>
                <input
                  value={formData.specifications.operatingSystem}
                  onChange={(e) => handleSpecChange('operatingSystem', e.target.value)}
                  placeholder="e.g., iOS 17, Android 14"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <FaCamera className="inline mr-2" /> Rear Camera
                </label>
                <input
                  value={formData.specifications.camera.rear}
                  onChange={(e) => handleCameraChange('rear', e.target.value)}
                  placeholder="e.g., 48MP Main + 12MP Ultra Wide"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Front Camera
                </label>
                <input
                  value={formData.specifications.camera.front}
                  onChange={(e) => handleCameraChange('front', e.target.value)}
                  placeholder="e.g., 12MP TrueDepth"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
            </div>
          </div>
        )}

        {/* Images Tab */}
        {activeTab === 'images' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <FaImage className="text-primary-600" /> Product Images
            </h2>
            
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 text-center">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                disabled={uploading}
                id="image-upload"
                className="hidden"
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer inline-flex flex-col items-center"
              >
                <div className="w-20 h-20 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                  <FaUpload className="text-3xl text-gray-500" />
                </div>
                <p className="text-gray-600 dark:text-gray-400 mb-2">
                  Click to upload product images
                </p>
                <p className="text-sm text-gray-500">
                  PNG, JPG, JPEG up to 5MB each
                </p>
              </label>
              {uploading && (
                <div className="mt-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Uploading...</p>
                </div>
              )}
            </div>
            
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mt-6">
                {formData.images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={img.url}
                      alt={img.altText}
                      className="w-full h-40 object-cover rounded-xl"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => handleSetPrimary(index)}
                        className="text-white text-xs bg-primary-600 px-2 py-1 rounded"
                      >
                        {img.isPrimary ? '★ Primary' : 'Set Primary'}
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveImage(index)}
                        className="text-white text-xs bg-red-600 px-2 py-1 rounded"
                      >
                        <FaTrash className="inline" /> Remove
                      </button>
                    </div>
                    {img.isPrimary && (
                      <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                        Primary
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Status Tab */}
        {activeTab === 'status' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
              <FaCheckCircle className="text-primary-600" /> Product Status
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">Featured Product</span>
                    <p className="text-sm text-gray-500">Show on homepage featured section</p>
                  </div>
                  <input
                    type="checkbox"
                    name="status.featured"
                    checked={formData.status.featured}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
                
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">Trending</span>
                    <p className="text-sm text-gray-500">Mark as trending product</p>
                  </div>
                  <input
                    type="checkbox"
                    name="status.trending"
                    checked={formData.status.trending}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
                
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">New Arrival</span>
                    <p className="text-sm text-gray-500">Show in new arrivals section</p>
                  </div>
                  <input
                    type="checkbox"
                    name="status.newArrival"
                    checked={formData.status.newArrival}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
                
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">PTA Approved</span>
                    <p className="text-sm text-gray-500">Device is PTA approved</p>
                  </div>
                  <input
                    type="checkbox"
                    name="ptaApproved"
                    checked={formData.ptaApproved}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
              </div>
              
              <div className="space-y-4">
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">Active</span>
                    <p className="text-sm text-gray-500">Product visible on website</p>
                  </div>
                  <input
                    type="checkbox"
                    name="status.active"
                    checked={formData.status.active}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
                
                <label className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-xl cursor-pointer">
                  <div>
                    <span className="font-medium text-gray-800 dark:text-white">Warranty Available</span>
                    <p className="text-sm text-gray-500">Product comes with warranty</p>
                  </div>
                  <input
                    type="checkbox"
                    name="warranty.available"
                    checked={formData.warranty.available}
                    onChange={handleChange}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                </label>
                
                {formData.warranty.available && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Warranty Duration
                    </label>
                    <input
                      type="text"
                      name="warranty.duration"
                      value={formData.warranty.duration}
                      onChange={handleChange}
                      placeholder="e.g., 1 Year, 6 Months"
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </form>
  );
};

export default ProductForm;